import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { usePageMeta } from "@/components/Share";

const today = () => new Date().toLocaleDateString("en-CA");
const day = (d) => new Date(`${d}T12:00:00`);

function EventRow({ e, past }) {
  const d = e.date ? day(e.date) : null;
  return (
    <div className={`grid grid-cols-[72px_1fr] sm:grid-cols-[110px_1fr_200px] gap-6 py-8 border-t border-[#e2dcd4] ${past ? "opacity-60" : ""}`} data-testid="event-item">
      <div className="text-[#1f402b]">
        <p className="font-display text-4xl sm:text-5xl leading-none">{d ? d.getDate() : "—"}</p>
        <p className="mt-1 font-mono-k text-[11px] uppercase tracking-widest text-[#c5903b]">{d ? d.toLocaleDateString("en-NZ", { month: "short", year: "numeric" }) : "TBC"}</p>
      </div>
      <div>
        <h3 className="text-2xl text-[#1a261f]">{e.title}</h3>
        <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono-k text-[11px] text-[#5a665e]">
          {e.location && <span className="flex items-center gap-1"><MapPin size={11} /> {e.location}</span>}
          {e.time && <span>{e.time}</span>}
        </p>
        {e.description && <p className="mt-3 text-[#5a665e] leading-relaxed">{e.description}</p>}
        {e.link && <a href={e.link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm text-[#1f402b] link-u" data-testid="event-link">More details</a>}
      </div>
      {e.cover_image && <img src={imgUrl(e.cover_image)} alt="" className="hidden sm:block w-full aspect-[4/3] object-cover" />}
    </div>
  );
}

export default function Events() {
  const [items, setItems] = useState(null);
  usePageMeta("Events", "Upcoming naturist events and open days around Aotearoa New Zealand.");
  useEffect(() => {
    api.get("/public/events").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  const upcoming = (items || []).filter((e) => !e.date || e.date >= today());
  const past = (items || []).filter((e) => e.date && e.date < today()).reverse();

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20" data-testid="events-page">
      <p className="eyebrow rise">Events</p>
      <h1 className="rise d1 mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1a261f]">Days worth gathering for.</h1>
      <div className="mt-14">
        {items === null ? <p className="text-sm text-[#5a665e]">Loading…</p>
          : upcoming.length === 0 ? (
            <div className="border border-[#e2dcd4] bg-white p-10 sm:p-14" data-testid="events-empty">
              <CalendarDays className="text-[#c5903b]" />
              <h2 className="mt-4 text-3xl text-[#1f402b]">Nothing on the calendar just yet.</h2>
              <p className="mt-3 text-[#5a665e] max-w-md leading-relaxed">Summer open days and club events are added here as they're confirmed. Know of one? Let us know.</p>
              <Link to="/contact" className="mt-6 inline-block bg-[#1f402b] text-white px-6 py-3 text-sm hover:bg-[#2c5a3d]" data-testid="events-suggest-link">Suggest an event</Link>
            </div>
          ) : upcoming.map((e) => <EventRow key={e.id} e={e} />)}
      </div>
      {past.length > 0 && (
        <div className="mt-20" data-testid="events-past">
          <p className="eyebrow mb-2">Past events</p>
          {past.map((e) => <EventRow key={e.id} e={e} past />)}
        </div>
      )}
    </div>
  );
}
